// Pure builder for the brand create/update request body.
//
// Extracted from the inline payload assembled in
// client/src/pages/brand-wizard.tsx (handleSubmit). The load-bearing invariant:
// every editable Basics/Tone/Guardrails/Research field is sent as its value OR
// `null` (and array fields as the array OR `null` when empty) so CLEARING a
// field in the UI actually persists as null instead of being omitted (omitted
// fields leave the previous value untouched). Centralizing this guarantees a
// future field can't accidentally use the "omit when empty" pattern and silently
// fail to clear. `server/__tests__/brandPersonaConfig.test.ts` locks it in.

export interface BrandColorSwatch {
  label: string;
  hex: string;
}

export interface BrandWizardForm {
  name: string;
  slug: string;
  category: string;
  url: string;
  logoUrl: string;
  adgenLogoUrl: string;
  summary: string;
  positioning: string;
  worldview: string;
  enemy: string;
  promise: string;
  founderStory: string;
  mission: string;
  toneSummary: string;
  approvedPhrases: string[];
  bannedPhrases: string[];
  proofHierarchy: string[];
  claimGuardrails: string[];
  nonNegotiableTruths: string[];
  visualRules: string;
  colorPalette: BrandColorSwatch[];
  categoryLanguage: string[];
  pricePositioning: string;
  targetAudienceSignals: string[];
  keyProducts: string[];
  strategicPriorities: string[];
  researchSources: string[];
  researchNotes: string;
  researchConfidence: number | null | undefined;
  socialProfiles: string;
  brandDesignSystem: unknown;
}

export interface BuildBrandPayloadOptions {
  isEditMode: boolean;
  workspaceId?: string;
}

export function buildBrandUpdatePayload(
  form: BrandWizardForm,
  opts: BuildBrandPayloadOptions,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    name: form.name.trim(),
    slug:
      form.slug ||
      form.name
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/(^-|-$)/g, ""),
  };

  if (!opts.isEditMode) payload.workspaceId = opts.workspaceId;

  payload.category = form.category || null;
  payload.url = form.url || null;
  payload.logoUrl = form.logoUrl || null;
  payload.adgenLogoUrl = form.adgenLogoUrl || null;
  payload.summary = form.summary || null;
  payload.positioning = form.positioning || null;
  payload.worldview = form.worldview || null;
  payload.enemy = form.enemy || null;
  payload.promise = form.promise || null;
  payload.founderStory = form.founderStory || null;
  payload.mission = form.mission || null;
  payload.toneSummary = form.toneSummary || null;
  payload.approvedPhrases = form.approvedPhrases.length ? form.approvedPhrases : null;
  payload.bannedPhrases = form.bannedPhrases.length ? form.bannedPhrases : null;
  payload.proofHierarchy = form.proofHierarchy.length ? form.proofHierarchy : null;
  payload.claimGuardrails = form.claimGuardrails.length ? form.claimGuardrails : null;
  payload.nonNegotiableTruths = form.nonNegotiableTruths.length
    ? form.nonNegotiableTruths
    : null;
  payload.visualRules = form.visualRules || null;
  const filteredPalette = form.colorPalette.filter((c) => c.label.trim() || c.hex.trim());
  payload.colorPalette = filteredPalette.length ? filteredPalette : null;

  payload.categoryLanguage = form.categoryLanguage.length ? form.categoryLanguage : null;
  payload.pricePositioning = form.pricePositioning || null;
  payload.targetAudienceSignals = form.targetAudienceSignals.length
    ? form.targetAudienceSignals
    : null;
  payload.keyProducts = form.keyProducts.length ? form.keyProducts : null;
  payload.strategicPriorities = form.strategicPriorities.length
    ? form.strategicPriorities
    : null;
  payload.researchSources = form.researchSources.length ? form.researchSources : null;
  payload.researchNotes = form.researchNotes || null;
  payload.researchConfidence =
    typeof form.researchConfidence === "number" ? form.researchConfidence : null;
  if (form.socialProfiles && form.socialProfiles.trim()) {
    const sp = form.socialProfiles.trim();
    try {
      payload.socialProfiles = JSON.parse(sp);
    } catch {
      payload.socialProfiles = sp;
    }
  } else {
    payload.socialProfiles = null;
  }

  payload.brandDesignSystem = form.brandDesignSystem;

  return payload;
}
